/**
 * Keeps Handlebars block tags inside tables from being destroyed by the browser.
 *
 * Templates loop over line items like this:
 *
 *   <tbody>{{#each line_items}}<tr>…</tr>{{/each}}</tbody>
 *
 * but an HTML parser does not allow bare text directly inside <table>, <tbody>
 * or <tr>. When the visual editor puts that HTML into a contentEditable element
 * and reads it back, the browser moves `{{#each …}}` and `{{/each}}` out in front
 * of the table and leaves the row outside any loop — so the line items print
 * blank (or as `NaN`).
 *
 * The fix is to hold those tags as HTML comments while the HTML lives in the
 * editor's DOM. Comments are legal anywhere in a table and stay exactly where
 * they were put:
 *
 *   <tbody><!--{{#each line_items}}--><tr>…</tr><!--{{/each}}--></tbody>
 *
 * `protectTableTags` is applied when HTML goes INTO the canvas; `restoreTableTags`
 * is applied when HTML comes back OUT, so what is stored is always the plain
 * Handlebars form that the code editor and the renderer expect.
 */

/** Elements whose direct children must be table structure, not text. */
const TABLE_STRUCTURE_TAGS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr', 'colgroup']);

/** Elements that never have a closing tag. */
const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
]);

/** Handlebars block tags: `{{#…}}`, `{{/…}}`, `{{^…}}` and `{{else}}`. */
const BLOCK_TAG_RE = /^\{\{\s*(?:[#/^]|else\b)/;

/** HTML comment | opening/closing tag | Handlebars expression. */
const TOKEN_RE = /<!--[\s\S]*?-->|<(\/?)([a-zA-Z][\w:-]*)\b[^>]*?(\/?)>|\{\{[\s\S]*?\}\}/g;

/** A block tag that this module wrapped in a comment. */
const PROTECTED_RE = /<!--(\{\{\s*(?:[#/^]|else\b)[^}]*\}\})-->/g;

/**
 * Wraps Handlebars block tags that sit directly inside table structure in HTML
 * comments. Block tags anywhere else (inside cells, paragraphs, divs, …) are left
 * as visible text. Safe to call repeatedly: tags that are already protected are
 * not wrapped again.
 */
export function protectTableTags(html: string): string {
  if (!html || !html.includes('{{')) return html;

  const openElements: string[] = [];

  return html.replace(
    TOKEN_RE,
    (match: string, closing?: string, tagName?: string, selfClosing?: string) => {
      // Existing comments (including ones we already added) pass through.
      if (match.startsWith('<!--')) return match;

      // Handlebars expression: wrap only block tags that sit in table structure.
      if (match.startsWith('{{')) {
        const parent = openElements[openElements.length - 1];
        return parent && TABLE_STRUCTURE_TAGS.has(parent) && BLOCK_TAG_RE.test(match)
          ? `<!--${match}-->`
          : match;
      }

      // Otherwise it is an element tag: keep track of what we are inside.
      const tag = (tagName ?? '').toLowerCase();
      if (closing) {
        const index = openElements.lastIndexOf(tag);
        if (index !== -1) openElements.length = index;
      } else if (!selfClosing && !VOID_TAGS.has(tag)) {
        openElements.push(tag);
      }
      return match;
    },
  );
}

/** Unwraps tags protected by {@link protectTableTags} back to plain Handlebars. */
export function restoreTableTags(html: string): string {
  if (!html || !html.includes('<!--{{')) return html;
  return html.replace(PROTECTED_RE, '$1');
}
